import { useCallback, useEffect, useRef, useState } from "react";
import { MessageSquare, X, Minus, Send, ShieldAlert, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/publico`;
const CLAVE_SESION = "upim_chat_publico_token";

const leerToken = () => {
  try {
    return sessionStorage.getItem(CLAVE_SESION) || "";
  } catch {
    return "";
  }
};
const guardarToken = (t) => {
  try {
    if (t) sessionStorage.setItem(CLAVE_SESION, t);
  } catch {
    /* modo privado del navegador: la conversación sólo vive en memoria */
  }
};

export const AsistenteFlotante = () => {
  const [abierto, setAbierto] = useState(false);
  const [inicio, setInicio] = useState(null);
  const [mensajes, setMensajes] = useState([]);
  const [texto, setTexto] = useState("");
  const [escribiendo, setEscribiendo] = useState(false);
  const [cerrada, setCerrada] = useState(false);
  const [error, setError] = useState("");
  const finRef = useRef(null);
  const entradaRef = useRef(null);

  useEffect(() => {
    fetch(`${API}/chat/bienvenida`)
      .then((r) => r.json())
      .then(setInicio)
      .catch(() => setInicio(null));
  }, []);

  // Conserva la conversación al navegar entre páginas (token firmado, sin PII).
  useEffect(() => {
    const token = leerToken();
    if (!token) return;
    fetch(`${API}/chat/historial?session_token=${encodeURIComponent(token)}`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.messages?.length) setMensajes(d.messages);
        if (d?.session_token) guardarToken(d.session_token);
        setCerrada(Boolean(d?.cerrada));
      })
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (abierto) {
      finRef.current?.scrollIntoView({ behavior: "smooth" });
      entradaRef.current?.focus();
    }
  }, [abierto, mensajes, escribiendo]);

  const enviar = useCallback(
    async (mensaje) => {
      const contenido = (mensaje ?? texto).trim();
      if (!contenido || escribiendo || cerrada) return;
      setTexto("");
      setError("");
      setMensajes((m) => [...m, { role: "user", content: contenido }]);
      setEscribiendo(true);
      try {
        const r = await fetch(`${API}/chat`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ message: contenido, session_token: leerToken() || null }),
        });
        const d = await r.json();
        if (!r.ok) {
          setError(d?.detail || "No pude responder ahora mismo. Intenta de nuevo en un momento.");
        } else {
          guardarToken(d.session_token);
          setMensajes((m) => [...m, { role: "assistant", content: d.respuesta, modo: d.modo }]);
          setCerrada(Boolean(d.cerrada));
        }
      } catch {
        setError("No pude conectar con el asistente. Revisa tu conexión e intenta de nuevo.");
      } finally {
        setEscribiendo(false);
      }
    },
    [texto, escribiendo, cerrada]
  );

  return (
    <>
      <style>{`
        @keyframes upimFlota { 0%,100% { transform: translateY(0); } 50% { transform: translateY(-6px); } }
        @keyframes upimPulso { 0% { box-shadow: 0 0 0 0 rgba(97,196,154,.55); } 70% { box-shadow: 0 0 0 16px rgba(97,196,154,0); } 100% { box-shadow: 0 0 0 0 rgba(97,196,154,0); } }
        .upim-fab { animation: upimFlota 4.5s ease-in-out 3, upimPulso 3s ease-out 4; }
        .upim-fab:hover, .upim-fab:focus-visible { animation: none; transform: translateY(-2px) scale(1.04); }
        @media (prefers-reduced-motion: reduce) { .upim-fab, .upim-panel { animation: none !important; transition: none !important; } }
        @keyframes upimEntra { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
        .upim-panel { animation: upimEntra .22s ease-out; }
        @keyframes upimPunto { 0%,80%,100% { opacity:.25 } 40% { opacity:1 } }
      `}</style>

      {!abierto && (
        <button
          type="button"
          data-testid="asistente-flotante-boton"
          aria-label="Abrir el Asistente Inteligente UPIM"
          title="Asistente Inteligente UPIM"
          onClick={() => setAbierto(true)}
          className="upim-fab group fixed z-[60] flex items-center gap-3 rounded-full border border-[rgba(97,196,154,0.55)] bg-[var(--upim-superficie-1)] px-4 py-3 text-[var(--upim-texto)] shadow-2xl transition-colors hover:border-[var(--upim-acento)] hover:bg-[var(--upim-superficie-2)] focus:outline-none focus-visible:ring-4 focus-visible:ring-[var(--upim-acento)]"
          style={{
            left: "max(16px, env(safe-area-inset-left, 0px))",
            bottom: "max(16px, env(safe-area-inset-bottom, 0px))",
          }}
        >
          <MessageSquare className="h-5 w-5 text-[var(--upim-acento)]" aria-hidden="true" />
          <span className="hidden text-sm font-medium sm:inline">Asistente Inteligente UPIM</span>
        </button>
      )}

      {abierto && (
        <section
          data-testid="asistente-flotante-panel"
          role="dialog"
          aria-modal="false"
          aria-label="Asistente Inteligente UPIM"
          className="upim-panel fixed z-[60] flex w-[calc(100vw-32px)] max-w-[380px] flex-col overflow-hidden rounded-2xl border border-[rgba(97,196,154,0.3)] bg-[var(--upim-superficie-1)] shadow-2xl sm:w-[380px]"
          style={{
            left: "max(16px, env(safe-area-inset-left, 0px))",
            bottom: "max(16px, env(safe-area-inset-bottom, 0px))",
            maxHeight: "min(70vh, 620px)",
          }}
        >
          <header className="flex items-center justify-between gap-2 border-b border-[var(--upim-borde)] bg-black px-4 py-3 text-[var(--upim-texto)]">
            <div className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4 text-[var(--upim-acento)]" aria-hidden="true" />
              <p className="text-sm font-semibold">Asistente Inteligente UPIM</p>
            </div>
            <div className="flex items-center gap-1">
              <button
                type="button"
                data-testid="asistente-minimizar"
                aria-label="Minimizar el asistente"
                onClick={() => setAbierto(false)}
                className="rounded p-1.5 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#61c49a]"
              >
                <Minus className="h-4 w-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                data-testid="asistente-cerrar"
                aria-label="Cerrar el asistente"
                onClick={() => setAbierto(false)}
                className="rounded p-1.5 hover:bg-white/10 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#61c49a]"
              >
                <X className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          </header>

          <div
            data-testid="asistente-historial"
            className="flex-1 space-y-3 overflow-y-auto bg-[var(--upim-superficie-1)] px-4 py-3"
            aria-live="polite"
          >
            <p className="flex items-start gap-2 rounded-lg border border-[rgba(97,196,154,0.28)] bg-[var(--upim-acento-suave)] p-2 text-xs text-[var(--upim-acento-2)]">
              <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              <span data-testid="asistente-aviso-privacidad">
                {inicio?.aviso_privacidad ||
                  "Este chat es público: no compartas contraseñas, documentos ni datos personales sensibles."}
              </span>
            </p>

            {mensajes.length === 0 && (
              <p className="rounded-lg border border-[var(--upim-borde)] bg-[var(--upim-superficie-2)] p-3 text-sm text-[var(--upim-texto-2)]">
                {inicio?.saludo ||
                  "Hola, soy el Asistente Inteligente de UPIM. Pregúntame sobre la institución, la afiliación o los cursos."}
              </p>
            )}

            {mensajes.map((m, i) => (
              <p
                key={i}
                data-testid={m.role === "user" ? "asistente-mensaje-usuario" : "asistente-mensaje-agente"}
                className={
                  m.role === "user"
                    ? "ml-auto max-w-[85%] whitespace-pre-wrap rounded-lg bg-[var(--upim-acento)] p-2.5 text-sm text-black"
                    : "max-w-[90%] whitespace-pre-wrap rounded-lg border border-[var(--upim-borde)] bg-[var(--upim-superficie-2)] p-2.5 text-sm text-[var(--upim-texto)]"
                }
              >
                {m.content}
              </p>
            ))}

            {escribiendo && (
              <p data-testid="asistente-escribiendo" className="flex items-center gap-1.5 text-xs text-[var(--upim-texto-3)]">
                <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                El asistente está escribiendo…
              </p>
            )}
            {error && (
              <p data-testid="asistente-error" className="rounded-lg border border-[rgba(252,165,165,0.3)] bg-[rgba(252,165,165,0.12)] p-2 text-xs text-[var(--u-red)]">
                {error}
              </p>
            )}
            <span ref={finRef} />
          </div>

          {mensajes.length === 0 && (inicio?.sugerencias || []).length > 0 && (
            <div data-testid="asistente-sugerencias" className="flex flex-wrap gap-2 border-t border-[var(--upim-borde)] bg-[var(--upim-superficie-2)] px-4 py-2.5">
              {inicio.sugerencias.slice(0, 4).map((s) => (
                <button
                  key={s}
                  type="button"
                  onClick={() => enviar(s)}
                  className="rounded-full border border-[var(--upim-borde-fuerte)] px-2.5 py-1 text-xs text-[var(--upim-texto-2)] transition-colors hover:border-[var(--upim-acento)] hover:text-[var(--upim-acento)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--upim-acento)]"
                >
                  {s}
                </button>
              ))}
            </div>
          )}

          <form
            onSubmit={(e) => {
              e.preventDefault();
              enviar();
            }}
            className="flex items-center gap-2 border-t border-[var(--upim-borde)] bg-[var(--upim-superficie-2)] px-3 py-2.5"
          >
            <input
              ref={entradaRef}
              data-testid="asistente-entrada"
              aria-label="Escribe tu pregunta para el Asistente Inteligente UPIM"
              value={texto}
              onChange={(e) => setTexto(e.target.value)}
              maxLength={inicio?.max_mensaje || 1000}
              disabled={cerrada}
              placeholder={cerrada ? "Conversación cerrada" : "Escribe tu pregunta…"}
              className="min-w-0 flex-1 rounded-full border border-[var(--upim-borde-fuerte)] bg-[var(--upim-superficie-1)] px-3.5 py-2 text-sm text-[var(--upim-texto)] placeholder:text-[var(--upim-texto-3)] focus:border-[var(--upim-acento)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--upim-acento)]"
            />
            <button
              type="submit"
              data-testid="asistente-enviar"
              aria-label="Enviar mensaje"
              disabled={escribiendo || cerrada || !texto.trim()}
              className="rounded-full bg-[var(--upim-acento)] p-2.5 text-black transition-colors hover:bg-[var(--upim-acento-2)] disabled:opacity-40 focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--upim-acento)]"
            >
              <Send className="h-4 w-4" aria-hidden="true" />
            </button>
          </form>
        </section>
      )}
    </>
  );
};

export default AsistenteFlotante;

